/**
 * Layout containers.
 *
 * Three of them — Section, Stack, Grid — and they are what make the builder modular. Every
 * one accepts arbitrary children through a slot, so a page is assembled rather than chosen
 * from a menu of fixed shapes.
 *
 * Following Elementor's V4 model rather than its legacy one: there is no Section/Column
 * distinction. A Section is a container pinned to the top level; a Stack is the same idea
 * nested. Layout belongs to the parent, sizing to the child.
 */

import {
  slotContainer,
  type BlockSignal,
  type BlockSpec,
  type IRNode,
  type Issue,
} from '@lp/shared';
import { paddingValue, readPadding, type PaddingValue } from '@lp/shared';
import { ALIGN_OPTIONS, SPACE_OPTIONS, TONE_OPTIONS } from '../shared/util.js';
import { containerCls, gridCls, sectionCls, stackCls } from '../shared/patterns.js';

/** Every container can hold every other block. That is the point. */
const ANY = [
  'Section',
  'Stack',
  'Grid',
  'Columns',
  'Heading',
  'TextBlock',
  'ButtonBlock',
  'ImageBlock',
  'IconBlock',
  'ListBlock',
  'DividerBlock',
  'CardBlock',
  'StatBlock',
  'EmbedBlock',
];

/* ================================================================ *
 * Section — a full-bleed band with a centred content column
 * ================================================================ */

export interface SectionProps extends Record<string, unknown> {
  tone: string;
  padding: PaddingValue;
  width: 'narrow' | 'normal' | 'wide' | 'full';
  align: 'left' | 'center' | 'right';
  gap: string;
  anchorId: string;
  content: unknown;
}

export const Section: BlockSpec<SectionProps> = {
  id: 'Section',
  label: 'Section',
  category: 'both',
  description: 'A full-width band. Everything on a page lives inside one.',

  fields: {
    tone: { type: 'radio', label: 'Background', options: [...TONE_OPTIONS] },
    padding: {
      type: 'padding',
      label: 'Padding',
      help: 'Unlink to set the page gutter separately from the vertical rhythm — a section usually wants far more space above and below than at the sides.',
    },
    width: {
      type: 'radio',
      label: 'Content width',
      help: 'Normal is 1080px, matching the live site.',
      options: [
        { label: 'Narrow', value: 'narrow' },
        { label: 'Normal', value: 'normal' },
        { label: 'Wide', value: 'wide' },
        { label: 'Full', value: 'full' },
      ],
    },
    align: { type: 'radio', label: 'Text alignment', options: [...ALIGN_OPTIONS] },
    gap: { type: 'select', label: 'Gap between children', options: [...SPACE_OPTIONS] },
    anchorId: { type: 'text', label: 'Anchor ID', help: 'For in-page links. Optional.' },
    content: { type: 'slot', label: 'Content', allow: ANY },
  },

  defaultProps: {
    tone: 'white',
    // Unlinked by default: a section's two axes genuinely differ — 96px of rhythm above and
    // below, a 20px gutter at the sides. Linking them would put 96px down each edge.
    padding: paddingValue('9', '5', false),
    width: 'normal',
    align: 'left',
    gap: '6',
    anchorId: '',
    content: [],
  },

  toIR(props): IRNode {
    const pad = readPadding(props.padding, paddingValue('9', '5', false));

    const inner = slotContainer(
      'content',
      'Block',
      'div',
      containerCls({
        width: props.width,
        gap: props.gap,
        align: props.align,
        padX: pad.x,
      }),
      'Container',
    );

    return {
      kind: 'el',
      wf: 'Section',
      tag: 'section',
      cls: sectionCls(props.tone, pad.y).map((c) =>
        typeof c === 'string' ? { name: c } : c,
      ),
      ...(props.anchorId ? { attr: { id: props.anchorId } } : {}),
      displayName: 'Section',
      children: [inner],
    };
  },

  validate(props): Issue[] {
    if (props.anchorId && !/^[A-Za-z][A-Za-z0-9_-]*$/.test(props.anchorId)) {
      return [
        {
          id: 'bad-anchor',
          severity: 'error',
          message: 'An anchor ID must start with a letter and contain only letters, numbers, dashes and underscores.',
          location: { fieldPath: 'anchorId' },
        },
      ];
    }
    return [];
  },
};

/* ================================================================ *
 * Stack — a flex row or column
 * ================================================================ */

export interface StackProps extends Record<string, unknown> {
  direction: 'row' | 'column';
  stackOnMobile: boolean;
  gap: string;
  justify: 'start' | 'center' | 'end' | 'between';
  align: 'start' | 'center' | 'end' | 'stretch';
  wrap: boolean;
  padding: PaddingValue;
  tone: string;
  radius: string;
  border: boolean;
  shadow: boolean;
  content: unknown;
}

export const Stack: BlockSpec<StackProps> = {
  // The id stays 'Stack' so documents saved before the rename still load. Only the label
  // changed — "Stack" read as a rival to Columns, when its real job is to be a styled box.
  id: 'Stack',
  label: 'Box',
  category: 'both',
  description:
    'A container you can give a background, padding and a border — how you build a card. ' +
    'Stacks its contents vertically. For side-by-side, use Columns.',

  fields: {
    direction: {
      type: 'radio',
      label: 'Direction',
      help: 'Row is for a line of small things — buttons, logos. For page layout, use Columns.',
      options: [
        { label: 'Stacked', value: 'column' },
        { label: 'Side by side', value: 'row' },
      ],
    },
    stackOnMobile: {
      type: 'boolean',
      label: 'Stack on mobile',
      help: 'Rows become columns below 767px. Almost always what you want.',
    },
    gap: { type: 'select', label: 'Gap', options: [...SPACE_OPTIONS] },
    justify: {
      type: 'select',
      label: 'Distribute',
      options: [
        { label: 'Start', value: 'start' },
        { label: 'Centre', value: 'center' },
        { label: 'End', value: 'end' },
        { label: 'Space between', value: 'between' },
      ],
    },
    align: {
      type: 'select',
      label: 'Align',
      options: [
        { label: 'Stretch', value: 'stretch' },
        { label: 'Start', value: 'start' },
        { label: 'Centre', value: 'center' },
        { label: 'End', value: 'end' },
      ],
    },
    wrap: { type: 'boolean', label: 'Wrap' },
    padding: { type: 'padding', label: 'Padding' },
    tone: { type: 'radio', label: 'Background', options: [...TONE_OPTIONS] },
    radius: {
      type: 'select',
      label: 'Corner radius',
      options: [
        { label: 'None', value: '' },
        { label: 'Small', value: 'sm' },
        { label: 'Medium', value: 'md' },
        { label: 'Large', value: 'lg' },
      ],
    },
    border: { type: 'boolean', label: 'Border' },
    shadow: { type: 'boolean', label: 'Shadow' },
    content: { type: 'slot', label: 'Content', allow: ANY },
  },

  defaultProps: {
    direction: 'column',
    stackOnMobile: true,
    gap: '5',
    justify: 'start',
    align: 'stretch',
    wrap: false,
    padding: paddingValue('0'),
    tone: '',
    radius: '',
    border: false,
    shadow: false,
    content: [],
  },

  toIR(props): IRNode {
    const pad = readPadding(props.padding);

    return slotContainer(
      'content',
      'Block',
      'div',
      [
        ...stackCls({
          row: props.direction === 'row',
          stackOnMobile: props.stackOnMobile,
          gap: props.gap,
          justify: props.justify,
          align: props.align,
          wrap: props.wrap,
        }),
        // Linked emits one all-round class; separate emits an axis each. Fewer classes on
        // the element in the common case, which keeps the Webflow style panel readable.
        ...(pad.linked && pad.y !== '0'
          ? [{ name: `is-pad-${pad.y}`, combo: true, parent: 'lp-box' }]
          : []),
        ...(!pad.linked && pad.y !== '0'
          ? [{ name: `is-pad-y-${pad.y}`, combo: true, parent: 'lp-box' }]
          : []),
        ...(!pad.linked && pad.x !== '0'
          ? [{ name: `is-pad-x-${pad.x}`, combo: true, parent: 'lp-box' }]
          : []),
        ...(props.tone ? [{ name: `is-bg-${props.tone}`, combo: true, parent: 'lp-box' }] : []),
        ...(props.radius ? [{ name: `is-radius-${props.radius}`, combo: true, parent: 'lp-box' }] : []),
        ...(props.border ? [{ name: 'is-border', combo: true, parent: 'lp-box' }] : []),
        ...(props.shadow ? [{ name: 'is-shadow-card', combo: true, parent: 'lp-box' }] : []),
      ],
      'Stack',
    );
  },
};

/* ================================================================ *
 * Grid — equal columns that collapse sensibly
 * ================================================================ */

export interface GridProps extends Record<string, unknown> {
  columns: string;
  gap: string;
  align: 'start' | 'center' | 'stretch';
  content: unknown;
}

export const Grid: BlockSpec<GridProps> = {
  id: 'Grid',
  label: 'Grid',
  category: 'both',
  description: 'Equal columns. Drops to two on tablet and one on mobile.',

  fields: {
    columns: {
      type: 'radio',
      label: 'Columns',
      options: [2, 3, 4, 5, 6].map((n) => ({ label: String(n), value: String(n) })),
    },
    gap: { type: 'select', label: 'Gap', options: [...SPACE_OPTIONS] },
    align: {
      type: 'select',
      label: 'Align items',
      options: [
        { label: 'Stretch', value: 'stretch' },
        { label: 'Start', value: 'start' },
        { label: 'Centre', value: 'center' },
      ],
    },
    content: { type: 'slot', label: 'Cells', allow: ANY },
  },

  defaultProps: { columns: '3', gap: '4', align: 'stretch', content: [] },

  toIR(props): IRNode {
    return slotContainer(
      'content',
      'Grid',
      'div',
      gridCls(props.columns, props.gap, props.align),
      'Grid',
    );
  },

  signals(): BlockSignal[] {
    return [];
  },
};

export { Columns } from './columns.js';
import { Columns as ColumnsBlock } from './columns.js';

export const layoutPrimitives = [Section, ColumnsBlock, Stack, Grid];
